import { useMemo, useState } from 'react';
import { Table, Button, Tag, Typography, Modal, Form, Input, Select, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useOrgTree } from '@/entities/organization/api/hooks';
import { useUserRecords, useCreateUser, useUpdateUser } from '@/entities/user/api/hooks';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { antTablePagination, antTableScroll } from '@/shared/constants/antTable';
import type { UserRecord, UserRole, Status } from '@/entities/user/model/userSchema';
import { APP_ROLE_OPTIONS, displayRoleName } from '@/shared/constants/userRoles';
import { flattenOrgTree } from '../orgTreeUtils';
import s from './UsersCard.module.scss';

const { Text } = Typography;

type UserFormValues = {
  name: string;
  adLogin: string;
  roleCode: UserRole;
  orgUnit: string;
};

export function UsersCard({ readOnly = false }: { readOnly?: boolean }) {
  const { data: usersFromApi, isLoading: usersLoading } = useUserRecords();
  const { data: orgNodes } = useOrgTree();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const [userModal, setUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [form] = Form.useForm<UserFormValues>();

  const users = usersFromApi ?? [];

  const orgFlat = useMemo(() => (orgNodes ? flattenOrgTree(orgNodes) : []), [orgNodes]);

  const orgUnitOptions = useMemo(() => {
    const depts = orgFlat.filter((n) => n.department).map((n) => n.department!);
    const unique = [...new Set(depts)].sort();
    return [
      { value: 'Все подразделения', label: 'Все подразделения' },
      { value: '—', label: '— (не привязан)' },
      ...unique.map((d) => ({ value: d, label: d })),
    ];
  }, [orgFlat]);

  const openCreate = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({
      name: '',
      adLogin: '',
      roleCode: 'viewer',
      orgUnit: '',
    });
    setUserModal(true);
  };

  const openEdit = (r: UserRecord) => {
    setEditingUser(r);
    form.setFieldsValue({
      name: r.name,
      adLogin: r.adLogin,
      roleCode: r.roleCode,
      orgUnit: r.orgUnit,
    });
    setUserModal(true);
  };

  const submitUser = () => {
    form.validateFields().then((v) => {
      if (editingUser) {
        updateUser.mutate(
          {
            id: editingUser.id,
            name: v.name,
            orgUnit: v.orgUnit,
            roleCode: v.roleCode,
            adLogin: editingUser.adLogin,
          },
          {
            onSuccess: () => {
              message.success('Данные пользователя сохранены');
              setUserModal(false);
              setEditingUser(null);
              form.resetFields();
            },
            onError: () => message.error('Не удалось сохранить'),
          },
        );
      } else {
        createUser.mutate(
          {
            name: v.name,
            adLogin: v.adLogin,
            orgUnit: v.orgUnit,
            roleCode: v.roleCode,
          },
          {
            onSuccess: () => {
              message.success('Пользователь создан');
              setUserModal(false);
              form.resetFields();
            },
            onError: () => message.error('Не удалось создать'),
          },
        );
      }
    });
  };

  const userColumns: ColumnsType<UserRecord> = [
    { title: 'Пользователь', dataIndex: 'name', key: 'name' },
    { title: 'Логин', dataIndex: 'adLogin', key: 'login' },
    {
      title: 'Роль',
      key: 'role',
      render: (_: unknown, r: UserRecord) => <Tag className={s['root__role-tag']}>{displayRoleName(r)}</Tag>,
    },
    { title: 'Орг. единица', dataIndex: 'orgUnit', key: 'org' },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (st: Status) => <StatusBadge status={st} label="Активен" />,
    },
    ...(!readOnly
      ? [
          {
            title: 'Действия',
            key: 'actions',
            width: 110,
            render: (_: unknown, r: UserRecord) => (
              <Button type="link" size="small" onClick={() => openEdit(r)}>
                Изменить
              </Button>
            ),
          } as ColumnsType<UserRecord>[number],
        ]
      : []),
  ];

  const modalLoading = createUser.isPending || updateUser.isPending;

  return (
    <>
      <div className={s['root__section']}>
        <div className={s['root__section-header']}>
          <div className={s['root__table-title']}>Пользователи и роли</div>
          {!readOnly && (
            <Button type="primary" onClick={openCreate}>
              + Пользователь
            </Button>
          )}
        </div>
        <Table
          loading={usersLoading}
          dataSource={users}
          columns={userColumns}
          rowKey="id"
          size="small"
          pagination={{ ...antTablePagination }}
          scroll={antTableScroll}
        />
      </div>

      <Modal
        title={editingUser ? 'Изменение пользователя' : 'Новый пользователь'}
        open={userModal}
        onCancel={() => {
          setUserModal(false);
          setEditingUser(null);
          form.resetFields();
        }}
        onOk={submitUser}
        confirmLoading={modalLoading}
        destroyOnClose
      >
        <Text type="secondary" className={s['root__modal-hint']}>
          Роль выберите из списка — цвет метки подставляется автоматически. Логин редактируется только при создании.
        </Text>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="name" label="Имя" rules={[{ required: true, message: 'Укажите имя' }]}>
            <Input placeholder="ФИО" />
          </Form.Item>
          <Form.Item name="adLogin" label="Логин" rules={[{ required: true, message: 'Укажите логин' }]}>
            <Input placeholder="латиница, точки" disabled={!!editingUser} />
          </Form.Item>
          <Form.Item name="roleCode" label="Роль" rules={[{ required: true, message: 'Выберите роль' }]}>
            <Select
              placeholder="Роль"
              options={APP_ROLE_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="orgUnit"
            label="Орг. единица"
            rules={[{ required: true, message: 'Выберите подразделение' }]}
          >
            <Select showSearch placeholder="Выберите подразделение" options={orgUnitOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
